import React, { useState } from "react";
import {
  Box, Accordion, AccordionSummary, AccordionDetails,
  Typography, Chip, Divider, Paper, List, ListItem, ListItemIcon, ListItemText
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FacebookIcon from "@mui/icons-material/Facebook";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import CategoryIcon from "@mui/icons-material/Category";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import QuickreplyIcon from "@mui/icons-material/Quickreply";
import BoltIcon from "@mui/icons-material/Bolt";
import PixelIcon from "@mui/icons-material/PhotoFilter";
import CampaignIcon from "@mui/icons-material/Campaign";
import { styled, useTheme } from "@mui/material/styles";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

const MainPaper = styled(Paper)(({ theme }) => ({
  flex: 1, padding: theme.spacing(3), margin: theme.spacing(1),
  overflowY: "auto", borderRadius: theme.shape.borderRadius, boxShadow: theme.shadows[1],
}));

const InfoBox = ({ children, type = "info" }) => {
  const colors = { info: "#e3f2fd", warn: "#fff8e1", ok: "#e8f5e9" };
  const icons = { info: <InfoOutlinedIcon sx={{ color: "#1565c0", mt: 0.2 }} />, warn: <WarningAmberIcon sx={{ color: "#f57f17", mt: 0.2 }} />, ok: <CheckCircleOutlineIcon sx={{ color: "#2e7d32", mt: 0.2 }} /> };
  return (
    <Box sx={{ backgroundColor: colors[type], borderRadius: 1, p: 1.5, display: "flex", gap: 1, mb: 1 }}>
      {icons[type]}
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
};

const Step = ({ n, text }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.5 }}>
    <Chip label={n} size="small" sx={{ minWidth: 24, height: 20, fontSize: "0.7rem", fontWeight: 700, flexShrink: 0 }} />
    <Typography variant="body2">{text}</Typography>
  </Box>
);

const sections = [
  {
    icon: <ShoppingBagIcon sx={{ color: "#7b1fa2" }} />,
    title: "Produtos",
    color: "#7b1fa2",
    content: (
      <Box>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Produtos representam o que você vende ou oferece. Cada produto pode ser vinculado a um <b>Pixel do Facebook</b> para rastrear conversões separadamente por produto.
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Como criar um produto:</Typography>
        <Step n="1" text='Acesse o menu lateral → "Produtos"' />
        <Step n="2" text='Clique em "Novo produto"' />
        <Step n="3" text="Preencha nome, descrição e imagem" />
        <Step n="4" text="Selecione o Pixel Facebook (opcional) — o evento de conversão será enviado para este pixel" />
        <Step n="5" text='Clique em "Salvar"' />
        <Divider sx={{ my: 1.5 }} />
        <InfoBox type="info">
          O pixel selecionado no produto deve ter sido previamente cadastrado em <b>Rastreamentos</b>. Não é possível digitar as credenciais do pixel diretamente no produto.
        </InfoBox>
        <InfoBox type="warn">
          Ao compartilhar um produto via código de importação, o pixel <b>não é copiado</b> — cada empresa precisa vincular o próprio pixel ao produto importado.
        </InfoBox>
      </Box>
    )
  },
  {
    icon: <PlayArrowIcon sx={{ color: "#1976d2" }} />,
    title: "Scripts",
    color: "#1976d2",
    content: (
      <Box>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Scripts são sequências de mensagens automáticas enviadas passo a passo para o contato. Cada passo pode ser: <b>texto, áudio, imagem, vídeo, arquivo ou etiqueta</b>.
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Vinculando script a um produto:</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Ao criar ou editar um script, selecione o produto ao qual ele pertence. Isso garante que quando o script disparar um evento de etiqueta, o evento seja enviado para o <b>pixel do produto</b>.
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Passos de tipo "Etiqueta":</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Quando o script executa um passo do tipo <b>Etiqueta</b>, o sistema verifica se aquela etiqueta tem um evento do Facebook vinculado. Se tiver, dispara o evento automaticamente para o pixel do produto do script.
        </Typography>
        <InfoBox type="ok">
          <b>Fluxo automático:</b> Script executa → passo "Etiqueta" → evento FB dispara → pixel do produto do script.
        </InfoBox>
        <InfoBox type="info">
          No painel de envio rápido (Speed Mode) no chat, é possível visualizar e enviar scripts diretamente. Cada passo mostra o tipo (Áudio, Imagem, etc.) e um botão de preview.
        </InfoBox>
      </Box>
    )
  },
  {
    icon: <CategoryIcon sx={{ color: "#e65100" }} />,
    title: "Categorias e Respostas Rápidas",
    color: "#e65100",
    content: (
      <Box>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          <b>Categorias</b> organizam as respostas rápidas em grupos. Uma resposta rápida pode pertencer a uma categoria para facilitar a busca no chat.
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Como usar:</Typography>
        <Step n="1" text='Crie categorias em "Categorias" no menu lateral' />
        <Step n="2" text='Ao criar uma resposta rápida, selecione a categoria desejada' />
        <Step n="3" text="No chat (Speed Mode), as respostas ficam agrupadas por categoria" />
        <Divider sx={{ my: 1.5 }} />
        <InfoBox type="info">
          As respostas rápidas aparecem no Speed Mode do chat com ícone de preview (olhinho). Clique para visualizar o conteúdo antes de enviar.
        </InfoBox>
      </Box>
    )
  },
  {
    icon: <PixelIcon sx={{ color: "#1877F2" }} />,
    title: "Rastreamentos — Pixels do Facebook",
    color: "#1877F2",
    content: (
      <Box>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Em <b>Rastreamentos</b> você cadastra os Pixels do Facebook (Conversions API). É possível ter múltiplos pixels por empresa.
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Campos do pixel:</Typography>
        <List dense>
          <ListItem><ListItemIcon><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon><ListItemText primary="Nome" secondary="Identificação interna (ex: Pixel Produto A)" /></ListItem>
          <ListItem><ListItemIcon><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon><ListItemText primary="Pixel ID" secondary="ID numérico do pixel no Facebook" /></ListItem>
          <ListItem><ListItemIcon><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon><ListItemText primary="Access Token (CAPI)" secondary="Token de acesso para a Conversions API" /></ListItem>
          <ListItem><ListItemIcon><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon><ListItemText primary="Código de teste (opcional)" secondary="Para testar eventos no painel 'Testar Eventos' do Facebook" /></ListItem>
        </List>
        <InfoBox type="warn">
          Para testar se os eventos estão chegando: coloque o código de teste no pixel, dispare um evento, e veja em tempo real no painel <b>Eventos do Gerenciador de Anúncios → Testar Eventos</b>.
        </InfoBox>
      </Box>
    )
  },
  {
    icon: <FlashOnIcon sx={{ color: "#ff6f00" }} />,
    title: "Eventos Facebook — Configuração",
    color: "#ff6f00",
    content: (
      <Box>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Em <b>Rastreamentos</b> você também configura os Eventos. Cada evento do Facebook (Lead, Purchase, etc.) pode ser vinculado a uma ou mais <b>etiquetas</b>.
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Como funciona:</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Quando a etiqueta vinculada é aplicada a um contato (via script), o sistema dispara automaticamente o evento para o Facebook.
        </Typography>
        <Step n="1" text='Crie um evento (ex: "Lead") em Rastreamentos → Eventos' />
        <Step n="2" text="Vincule as etiquetas que devem disparar esse evento" />
        <Step n="3" text="Ative o evento" />
        <Divider sx={{ my: 1.5 }} />
        <InfoBox type="ok">
          <b>Para qual pixel vai o evento?</b> Depende do contexto — veja a seção "Lógica de disparo" abaixo.
        </InfoBox>
      </Box>
    )
  },
  {
    icon: <BoltIcon sx={{ color: "#c62828" }} />,
    title: "Lógica de disparo de eventos — Para qual pixel?",
    color: "#c62828",
    content: (
      <Box>
        <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>
          A plataforma define automaticamente para qual pixel enviar o evento, seguindo esta hierarquia:
        </Typography>

        <Box sx={{ mb: 2, p: 2, backgroundColor: "#e8f5e9", borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ color: "#2e7d32", mb: 0.5 }}>1. Via Script (automático)</Typography>
          <Typography variant="body2">
            Quando um script é executado e um passo de etiqueta dispara um evento, o evento vai para o <b>pixel do produto vinculado ao script</b>.
          </Typography>
          <Typography variant="caption" color="textSecondary">Script → Produto → Pixel do produto → Evento disparado apenas para esse pixel</Typography>
        </Box>

        <Box sx={{ mb: 2, p: 2, backgroundColor: "#e3f2fd", borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ color: "#1565c0", mb: 0.5 }}>2. Disparo manual no chat</Typography>
          <Typography variant="body2">
            Ao clicar no botão de rastreamento (ícone) no chat, abre um modal onde você escolhe o evento e pode <b>selecionar um pixel específico</b>.
            Se deixar em branco, dispara para <b>todos os pixels ativos</b> da empresa.
          </Typography>
        </Box>

        <Box sx={{ mb: 2, p: 2, backgroundColor: "#fff8e1", borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ color: "#f57f17", mb: 0.5 }}>3. Etiqueta aplicada manualmente (sem script)</Typography>
          <Typography variant="body2">
            Quando uma etiqueta é aplicada diretamente ao contato fora de um script, o evento vai para <b>todos os pixels ativos</b> da empresa, pois não há contexto de produto.
          </Typography>
          <Typography variant="caption" color="textSecondary">Dica: vincule os scripts aos produtos para aproveitar o rastreamento por pixel específico.</Typography>
        </Box>

        <Box sx={{ p: 2, backgroundColor: "#fce4ec", borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ color: "#880e4f", mb: 0.5 }}>Fallback — Nenhum pixel configurado no produto</Typography>
          <Typography variant="body2">
            Se o produto não tiver pixel vinculado, o evento dispara para <b>todos os pixels ativos</b> da empresa normalmente.
          </Typography>
        </Box>
      </Box>
    )
  },
  {
    icon: <QuickreplyIcon sx={{ color: "#00838f" }} />,
    title: "Speed Mode — Painel de envio rápido no chat",
    color: "#00838f",
    content: (
      <Box>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          O Speed Mode é o painel lateral no chat que permite enviar respostas rápidas, scripts e produtos sem precisar digitar.
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>O que aparece no Speed Mode:</Typography>
        <List dense>
          <ListItem><ListItemIcon><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon><ListItemText primary="Respostas Rápidas" secondary="Agrupadas por categoria. Clique no olhinho para visualizar antes de enviar." /></ListItem>
          <ListItem><ListItemIcon><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon><ListItemText primary="Scripts" secondary="Mostra os passos do script com o tipo (Áudio, Imagem, etc.). Clique para expandir e visualizar." /></ListItem>
          <ListItem><ListItemIcon><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon><ListItemText primary="Produtos" secondary="Envie informações do produto diretamente para o contato." /></ListItem>
        </List>
        <InfoBox type="info">
          Para ver o preview de um passo de áudio ou imagem, clique no ícone de olho ao lado do passo no Speed Mode.
        </InfoBox>
        <InfoBox type="warn">
          O ícone de preview só aparece para passos que têm mídia (áudio, imagem, vídeo ou arquivo). Passos de texto e etiqueta não têm preview.
        </InfoBox>
      </Box>
    )
  },
  {
    icon: <CampaignIcon sx={{ color: "#6a1b9a" }} />,
    title: "Fluxos de Remarketing",
    color: "#6a1b9a",
    content: (
      <Box>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Fluxos de Remarketing criam sequências automáticas de mensagens acionadas quando uma etiqueta é aplicada ao contato. Configure gatilhos, intervalos entre mensagens e ações para nutrir leads e reengajar clientes automaticamente.
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Como funciona:</Typography>
        <Step n="1" text="Acesse Fluxos de Remarketing no menu lateral" />
        <Step n="2" text="Crie um fluxo e vincule uma etiqueta gatilho" />
        <Step n="3" text="Adicione mensagens com intervalos de tempo entre elas" />
        <Step n="4" text="Quando a etiqueta for aplicada ao contato, o fluxo inicia automaticamente" />
        <Divider sx={{ my: 1.5 }} />
        <InfoBox type="ok">O fluxo é cancelado se uma etiqueta de cancelamento for aplicada ou se o contato responder (conforme configuração).</InfoBox>
        <InfoBox type="info">Combine com eventos do Facebook para rastrear cada etapa do funil de vendas.</InfoBox>
      </Box>
    )
  }
];

const Guide = () => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InfoOutlinedIcon sx={{ color: theme.palette.primary.main }} />
            Guia da Plataforma
          </Box>
        </Title>
      </MainHeader>
      <MainPaper variant="outlined">
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Referência rápida sobre os recursos da plataforma. Clique em cada seção para expandir.
        </Typography>
        {sections.map((s, i) => (
          <Accordion key={i} expanded={expanded === i} onChange={() => setExpanded(expanded === i ? false : i)}
            sx={{ mb: 1, borderRadius: "8px !important", "&:before": { display: "none" }, boxShadow: theme.shadows[1] }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {s.icon}
                <Typography fontWeight={700} sx={{ color: s.color }}>{s.title}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Divider sx={{ mb: 1.5 }} />
              {s.content}
            </AccordionDetails>
          </Accordion>
        ))}
      </MainPaper>
    </MainContainer>
  );
};

export default Guide;
